import Decimal from 'decimal.js';
import groupBy from 'lodash/groupBy';

import { AssetToken } from '@src/types/common/token.types';
import { PositionManagerInfoItem } from '@src/types/vaults/positionManagerInfo.types';
import {
  PendingRewardsType,
  PendingRewardsWithAccountType,
  VaultFarmsType,
} from '@src/types/vaults/vault.types';
import { Address } from 'viem';

import {
  PERCENT_DECIMALS,
  normalizePercent,
} from '@helpers/vaults/priceRange.helpers';

import { applyDecimals, toBN } from '@utils/formaters.utils';
import { getTokenListForUi, isAddressesEqual } from '@utils/tokens.utils';

import { ZERO_ADDRESS } from '@constants/common/chain.constants';
import { PERCENT_DIVIDER } from '@constants/common/common.constants';
import { AssetType } from '@constants/common/tokens.constants';
import { TokenType, V3_TOKENS_TYPE } from '@constants/vaults/tokens.constants';
import {
  ConnectorRangeWidthType,
  FarmTag,
  PRICE_IMPACT_LEVEL_VALUE,
  PriceImpactLevel,
  QUICK_ACTIONS_CONFIG,
  QUICK_ACTIONS_TITLE,
} from '@constants/vaults/vault.constants';

import { CommonTokenDto } from '@dto/common/tokens.dto';
import { TokensDto } from '@dto/vaults/tokens.dto';
import { FarmDto, VaultFactoryDto } from '@dto/vaults/vault-factory.dto';
import { LegacyVaultsDto, VaultDto } from '@dto/vaults/vaults.dto';

type NormalizeVaultFarmsInfoParamType = {
  vault: LegacyVaultsDto;
  factoryFarms: Record<number, FarmDto>;
  tokens: TokensDto | undefined;
};

export const normalizeVaultFarmsInfo = ({
  vault,
  factoryFarms,
  tokens,
}: NormalizeVaultFarmsInfoParamType): VaultFarmsType[] => {
  if (!tokens || !factoryFarms) return [];

  const factoryTokens = tokens.farm[vault.chainId];
  const accountShare = vault.accountBalance
    ? toBN(vault.accountBalance).div(vault.totalSupply)
    : toBN(0);

  const tvlInUsd = vault.vaultFarms.reduce(
    (acc, item) => acc.plus(item.stakedUsd),
    toBN(0),
  );

  const vaultFarms = vault.vaultFarms.map((farm) => {
    const farmInfo = factoryFarms[farm.farmId];

    const isV3 = V3_TOKENS_TYPE.includes(farmInfo.stakingTokenType);

    const stakeTokenAddress = isV3
      ? farmInfo?.poolV3?.toLowerCase()
      : farmInfo.stakingToken.toLowerCase();
    const stakingToken = factoryTokens[stakeTokenAddress as string];
    const rewardTokens = farmInfo.rewardTokens.map(
      (rewardToken) => factoryTokens[rewardToken],
    );

    const stakedBalance = toBN(farm.stakedBalance || 0);
    const accountStakeBalance = stakedBalance.times(accountShare);

    const stakedUsd = toBN(farm.stakedUsd || 0);
    const accountStakedUsd = stakedUsd.times(accountShare);

    const normalizeFarmPercent = toBN(farm.percent).times(100);

    const currentStakedPercent =
      !tvlInUsd.isZero() && !stakedUsd.isZero()
        ? toBN(stakedUsd).dividedBy(tvlInUsd).times(100)
        : toBN(0);

    return {
      farm: {
        ...farm,
        stakedBalance,
        stakedUsd,
        accountStakeBalance,
        accountStakedUsd,
        normalizeFarmPercent,
        currentStakedPercent,
      },
      farmInfo: {
        ...farmInfo,
        stakingTokenAddress: farmInfo.stakingToken,
        stakingToken,
        rewardTokens,
        isV3,
      },
    };
  });
  return vaultFarms.sort((a, b) => b.farm.percent - a.farm.percent);
};

type NormalizeZapperTokensParamType = {
  vault: VaultDto;
  vaultFactory: VaultFactoryDto;
  tokens: TokensDto | undefined;
};

export const normalizeZapperTokens = ({
  vault,
  vaultFactory,
  tokens,
}: NormalizeZapperTokensParamType): CommonTokenDto[] => {
  if (!vaultFactory || !tokens) return [];
  const commonTokensResult = tokens.common[vault.chainId];
  const zapperTokensResult = tokens.zapper[vault.chainId];
  const wlTokens = [ZERO_ADDRESS, ...vaultFactory.underlyingZapperTokensWl];

  return wlTokens
    .map(
      (item) =>
        commonTokensResult[item.toLowerCase()] ||
        zapperTokensResult[item.toLowerCase()],
    )
    .sort((a) => (a.address === ZERO_ADDRESS ? -1 : 0));
};

export const isV3orLPToken = (tokenType: TokenType) =>
  tokenType === TokenType.LP || V3_TOKENS_TYPE.includes(tokenType);

interface PrepareTokensParamType {
  tokens: CommonTokenDto[];
  assetType: AssetType;
  balances: {
    address: Address;
    chainId: number;
    balance: Decimal;
  }[];
  native: Decimal;
}

export const prepareTokensForSelect = ({
  tokens,
  assetType,
  balances,
  native,
}: PrepareTokensParamType): AssetToken[] =>
  tokens.map((token) => {
    const {
      address,
      chainId,
      token1,
      token0,
      type,
      symbol,
      priceInUsd,
      decimals,
    } = token;

    const balanceObj = balances.find((item) =>
      isAddressesEqual(item.address, address),
    );

    const balance = isAddressesEqual(address, ZERO_ADDRESS)
      ? native
      : balanceObj?.balance || toBN(0);

    const formattedBalance = toBN(balance, decimals);
    const balanceInUsd = formattedBalance.times(priceInUsd);

    return {
      icons: getTokenListForUi({
        token0,
        token1,
        chainId,
        address,
        type,
      }),
      symbol,
      assetType,
      balance,
      balanceInUsd,
      fee: null,
      tokenId: null,
      address,
      decimals,
      priceInUsd,
    };
  });

type NormalizeDepositTokensParamType = {
  zapperTokens: CommonTokenDto[];
  farmsV2StakingTokens: CommonTokenDto[];
  farmsV3StakingTokens: CommonTokenDto[];
  nativeBalance: Decimal;
  tokens: TokensDto | undefined;
  userNftInfo: PositionManagerInfoItem[];
  depositTokensBalances: {
    balance: Decimal;
    address: Address;
    chainId: number;
  }[];
};

export const normalizeDepositTokens = ({
  zapperTokens,
  farmsV2StakingTokens,
  userNftInfo,
  nativeBalance,
  depositTokensBalances,
  farmsV3StakingTokens,
  tokens,
}: NormalizeDepositTokensParamType) => {
  const zapperTokensResult = prepareTokensForSelect({
    tokens: zapperTokens,
    balances: depositTokensBalances,
    assetType: AssetType.Zapper,
    native: nativeBalance,
  });

  const lpTokensResult = prepareTokensForSelect({
    tokens: farmsV2StakingTokens,
    balances: depositTokensBalances,
    assetType: AssetType.Lp,
    native: nativeBalance,
  });

  const nftTokensResult = (): AssetToken[] => {
    if (userNftInfo.length) {
      return userNftInfo.map((item) => ({
        icons: [
          { address: item.token0.address, chainId: item.token0.chainId },
          { address: item.token1.address, chainId: item.token1.chainId },
        ],
        symbol: `${item.token0.symbol}/${item.token1.symbol}`,
        fee: item.poolV3Fee,
        tokenId: item.tokenId,
        balanceInUsd: item.liquidity,
        assetType: AssetType.Nft,
        balance: null,
        address: null,
        decimals: null,
        priceInUsd: null,
      }));
    }

    if (!tokens) return [];

    return farmsV3StakingTokens.map((farmToken) => {
      const chainTokens = tokens.poolv3[farmToken.chainId];
      const token0 = chainTokens[farmToken?.token0?.toLowerCase() as string];
      const token1 = chainTokens[farmToken?.token1?.toLowerCase() as string];

      return {
        icons: [
          { address: token0.address, chainId: token0.chainId },
          { address: token1.address, chainId: token1.chainId },
        ],
        assetType: AssetType.Nft,
        balanceInUsd: null,
        symbol: `${token0.symbol}/${token1.symbol}`,
        fee: 0,
        balance: null,
        tokenId: null,
        address: null,
        decimals: null,
        priceInUsd: null,
      };
    });
  };

  return [...zapperTokensResult, ...lpTokensResult, ...nftTokensResult()].sort(
    (a, b) => {
      const aBalanceInUsd = toBN(a.balanceInUsd || 0);
      const bBalanceInUsd = toBN(b.balanceInUsd || 0);
      return toBN(bBalanceInUsd).minus(toBN(aBalanceInUsd)).toNumber();
    },
  );
};

type NormalizeWithdrawTokensParamType = {
  nativeBalance: Decimal;
  zapperTokens: CommonTokenDto[];
  tokens: TokensDto | undefined;
  vaultFarms: VaultFarmsType[];
  tokensBalances: { balance: Decimal; address: Address; chainId: number }[];
};

export const normalizeWithdrawTokens = ({
  zapperTokens,
  nativeBalance,
  tokensBalances,
  vaultFarms,
  tokens,
}: NormalizeWithdrawTokensParamType) => {
  if (!tokens) return [];

  const zapperTokensResult = prepareTokensForSelect({
    tokens: zapperTokens,
    balances: tokensBalances,
    assetType: AssetType.Zapper,
    native: nativeBalance,
  });

  const farmsV2StakingTokens = vaultFarms.reduce((acc, farm) => {
    if (!V3_TOKENS_TYPE.includes(farm.farmInfo.stakingToken.type)) {
      return [...acc, farm.farmInfo.stakingToken];
    }
    return acc;
  }, [] as CommonTokenDto[]);

  const lpTokensResult = prepareTokensForSelect({
    tokens: farmsV2StakingTokens,
    balances: tokensBalances,
    assetType: AssetType.Lp,
    native: nativeBalance,
  });

  const farmsV3StakingTokens = vaultFarms.filter((farm) =>
    V3_TOKENS_TYPE.includes(farm.farmInfo.stakingToken.type),
  );

  const nftTokensResult: AssetToken[] = farmsV3StakingTokens.map(
    ({ farm, farmInfo }) => {
      const { stakingToken } = farmInfo;
      const { poolV3PositionId } = farm;

      const chainTokens = tokens.poolv3[stakingToken.chainId];
      const token0 = chainTokens[stakingToken?.token0?.toLowerCase() as string];
      const token1 = chainTokens[stakingToken?.token1?.toLowerCase() as string];

      return {
        icons: [
          { address: token0.address, chainId: token0.chainId },
          { address: token1.address, chainId: token1.chainId },
        ],
        symbol: `${token0.symbol}/${token1.symbol}`,
        fee: farmInfo?.poolV3Fee || null,
        tokenId: poolV3PositionId || null,
        balanceInUsd: null,
        assetType: AssetType.Nft,
        balance: null,
        address: null,
        decimals: null,
        priceInUsd: null,
      };
    },
  );

  const emergencyResult: AssetToken = {
    icons: [],
    symbol: 'Emergency',
    fee: null,
    tokenId: null,
    balanceInUsd: null,
    assetType: AssetType.Emergency,
    balance: null,
    address: null,
    decimals: null,
    priceInUsd: null,
  };

  return [
    ...zapperTokensResult,
    ...lpTokensResult,
    ...nftTokensResult,
    emergencyResult,
  ].sort((a, b) => {
    const aBalanceInUsd = toBN(a.balanceInUsd || 0);
    const bBalanceInUsd = toBN(b.balanceInUsd || 0);
    return toBN(bBalanceInUsd).minus(toBN(aBalanceInUsd)).toNumber();
  });
};

export const getPriceImpactLevel = (priceImpact: Decimal) => {
  switch (true) {
    case priceImpact.gte(PRICE_IMPACT_LEVEL_VALUE[PriceImpactLevel.High]):
      return PriceImpactLevel.High;
    case priceImpact.gte(PRICE_IMPACT_LEVEL_VALUE[PriceImpactLevel.Medium]):
      return PriceImpactLevel.Medium;
    case priceImpact.gt(PRICE_IMPACT_LEVEL_VALUE[PriceImpactLevel.Zero]) &&
      priceImpact.lt(PRICE_IMPACT_LEVEL_VALUE[PriceImpactLevel.Medium]):
      return PriceImpactLevel.Low;
    default:
      return PriceImpactLevel.Zero;
  }
};

type NormalizePendingRewardsParamType = {
  farmsPendingRewards: PendingRewardsType[];
  feePendingRewards: PendingRewardsType[];
  vault: VaultDto;
};

export const normalizePendingRewards = ({
  farmsPendingRewards,
  feePendingRewards,
  vault,
}: NormalizePendingRewardsParamType): PendingRewardsWithAccountType[] => {
  if (!farmsPendingRewards.length && !feePendingRewards.length) return [];

  const multiplier = vault.accountBalance
    ? toBN(vault.accountBalance).div(vault.totalSupply)
    : toBN(1);

  const rewardTokens = [...farmsPendingRewards, ...feePendingRewards];

  const accountRewardTokens = rewardTokens.map((item) => ({
    ...item,
    accountPendingRewards: item.pendingRewards.times(multiplier),
    accountPendingRewardsUsd: item.pendingRewardsUsd.times(multiplier),
  }));

  const grouppedTokens = Object.values(
    groupBy(accountRewardTokens, (item) => item.rewardToken.address),
  );

  return grouppedTokens.map((item) =>
    item.reduce(
      (acc, row) => ({
        ...acc,
        rewardToken: row.rewardToken,
        pendingRewards: acc?.pendingRewards
          ? acc.pendingRewards.plus(row.pendingRewards)
          : row.pendingRewards,
        pendingRewardsUsd: acc?.pendingRewardsUsd
          ? acc.pendingRewardsUsd.plus(row.pendingRewardsUsd)
          : row.pendingRewardsUsd,
        accountPendingRewards: acc?.accountPendingRewards
          ? acc.accountPendingRewards.plus(row.accountPendingRewards)
          : row.accountPendingRewards,
        accountPendingRewardsUsd: acc?.accountPendingRewardsUsd
          ? acc.accountPendingRewardsUsd.plus(row.accountPendingRewardsUsd)
          : row.accountPendingRewardsUsd,
      }),
      {} as PendingRewardsWithAccountType,
    ),
  );
};

export const toggleFilterState = <T extends string | number>(
  prevSelected: T[],
  value: T,
  allValues: T[],
): T[] => {
  if (prevSelected.length === allValues.length) {
    return [value];
  }

  if (prevSelected.length === 1 && prevSelected.includes(value)) {
    return allValues;
  }

  if (prevSelected.includes(value)) {
    return prevSelected.filter((item) => item !== value);
  }

  return [...prevSelected, value];
};

export const getPriceRatioAtoB = (
  sqrtPriceX96: string,
  tokenA: CommonTokenDto,
  tokenB: CommonTokenDto,
) => {
  if (!sqrtPriceX96 || !tokenA || !tokenB) {
    return {
      rateTokenAToTokenB: toBN(0),
      rateTokenBToTokenA: toBN(0),
    };
  }
  const FIXED_POINT_FACTOR = BigInt(1) << BigInt(192);
  const amountTokenA = applyDecimals(1, tokenA.decimals);
  const amountTokenB = applyDecimals(1, tokenB.decimals);

  const rateTokenAToTokenB = amountTokenA
    .times(toBN(sqrtPriceX96).pow(2))
    .div(FIXED_POINT_FACTOR.toString())
    .div(amountTokenB);

  const rateTokenBToTokenA = amountTokenB
    .times(toBN(sqrtPriceX96).pow(2))
    .div(FIXED_POINT_FACTOR.toString())
    .div(amountTokenA);

  return {
    rateTokenAToTokenB,
    rateTokenBToTokenA,
  };
};

interface TokenWeights {
  amount0: bigint;
  amount1: bigint;
  token0: CommonTokenDto;
  token1: CommonTokenDto;
}

export const getTokenWeights = ({
  amount0,
  token0,
  amount1,
  token1,
}: TokenWeights) => {
  const balance0 = token0 ? toBN(amount0, token0.decimals) : toBN(0);
  const balance1 = token1 ? toBN(amount1, token1.decimals) : toBN(0);

  const amount0Usd = token0 ? balance0.times(token0.priceInUsd) : toBN(0);
  const amount1Usd = token1 ? balance1.times(token1.priceInUsd) : toBN(0);

  const sumAmountUsd = amount0Usd.plus(amount1Usd);

  const token0Weight = amount0Usd.div(sumAmountUsd);
  const token1Weight = amount1Usd.div(sumAmountUsd);

  return {
    token0Weight,
    token1Weight,
    sumAmountUsd,
    amount0Usd,
    amount1Usd,
    balance0,
    balance1,
  };
};

export const inversePercent = (minPercent: Decimal, maxPercent: Decimal) => {
  const minCoeff = toBN(1).plus(toBN(minPercent).dividedBy(PERCENT_DIVIDER));
  const maxCoeff = toBN(1).plus(toBN(maxPercent).dividedBy(PERCENT_DIVIDER));

  let inverseMinCoefficient = toBN(1).dividedBy(maxCoeff);
  let inverseMaxCoefficient = toBN(1).dividedBy(minCoeff);

  if (!inverseMinCoefficient.isFinite()) {
    inverseMinCoefficient = toBN(0);
  }
  if (!inverseMaxCoefficient.isFinite()) {
    inverseMaxCoefficient = toBN(101);
  }

  const inverseMin = inverseMinCoefficient
    .minus(1)
    .times(PERCENT_DIVIDER)
    .toFixed(PERCENT_DECIMALS);
  const inverseMax = inverseMaxCoefficient
    .minus(1)
    .times(PERCENT_DIVIDER)
    .toFixed(PERCENT_DECIMALS);

  return { min: toBN(inverseMin), max: toBN(inverseMax) };
};

export const generateQuickButtonsConfig = (
  farmTag: FarmTag,
  isInverseMode: boolean,
) => {
  const pairTypeConfig = QUICK_ACTIONS_CONFIG[farmTag];

  return Object.entries(pairTypeConfig).map(([rangeType, rangeValues]) => {
    const min = normalizePercent(rangeValues.low).times(-1);
    const max = normalizePercent(rangeValues.up);

    const inverseData = inversePercent(min, max);

    const percentage = isInverseMode
      ? ConnectorRangeWidthType.Full === rangeType
        ? { min, max }
        : { min: inverseData.min, max: inverseData.max }
      : {
          min: toBN(min.toFixed(PERCENT_DECIMALS)),
          max: toBN(max.toFixed(PERCENT_DECIMALS)),
        };

    return {
      title: QUICK_ACTIONS_TITLE[rangeType as ConnectorRangeWidthType],
      ...percentage,
      rangeWidthType: rangeType as ConnectorRangeWidthType,
    };
  });
};

export const getPriorityFarmTag = (
  farm: VaultFarmsType | undefined,
): FarmTag => {
  if (!farm) return FarmTag.Default;

  const {
    isStablePair,
    isCorrelatedPair,
    isEthBtcPair,
    isBlueChipPair,
    isVolatilePair,
  } = farm.farmInfo;

  if (isStablePair) return FarmTag.Stable;
  if (isCorrelatedPair) return FarmTag.Correlated;
  if (isEthBtcPair) return FarmTag.EthBtc;
  if (isBlueChipPair) return FarmTag.BlueChip;
  if (isVolatilePair) return FarmTag.Volatile;
  return FarmTag.Default;
};

export const getV3FarmTags = (farm: VaultFarmsType) => {
  const {
    isStablePair,
    isCorrelatedPair,
    isEthBtcPair,
    isBlueChipPair,
    isVolatilePair,
  } = farm.farmInfo;

  const flags: Record<FarmTag, boolean> = {
    [FarmTag.Stable]: isStablePair,
    [FarmTag.Correlated]: isCorrelatedPair,
    [FarmTag.EthBtc]: isEthBtcPair,
    [FarmTag.BlueChip]: isBlueChipPair,
    [FarmTag.Volatile]: isVolatilePair,
    [FarmTag.Default]: false,
  };

  return (Object.keys(flags) as FarmTag[]).filter((key) => flags[key]);
};

export function isConnectorRangeWidthType(
  value: unknown,
): value is ConnectorRangeWidthType {
  return (
    typeof value === 'string' &&
    Object.values(ConnectorRangeWidthType).includes(
      value as ConnectorRangeWidthType,
    )
  );
}

export const determineInitialMode = (
  farmTag: FarmTag,
  initialMin: number,
  initialMax: number,
): ConnectorRangeWidthType | null => {
  if (!initialMin || !initialMax || !farmTag) return null;

  const pairTypeConfig = QUICK_ACTIONS_CONFIG[farmTag];

  return (
    (
      Object.entries(pairTypeConfig) as [
        ConnectorRangeWidthType,
        { low: string; up: string },
      ][]
    ).find(([, rangeValues]) => {
      const min = normalizePercent(rangeValues.low).times(-1);
      const max = normalizePercent(rangeValues.up);
      return min.lte(initialMin) && max.gte(initialMax);
    })?.[0] || null
  );
};

export const removeAnyTabParam = (
  query: string,
  removedParam: string,
): string => {
  return query
    .split('&')
    .filter((param) => !param.startsWith(removedParam))
    .join('&');
};
